import { getDisplayName } from '@juvare/contact-management/backend/features/accounts/shared';
import { defineEntity } from '@juvare/platform-app/App';
import { defineColumns } from '@juvare/platform-shared/features/storage';
export const externalRecipientsGroup = 'ImportedExternalRecipient';
export const externalIdPrefixForAlertService = 'external_';
const externalRecipientColumns = defineColumns({
    importId: {
        type: 'id-required',
        index: true,
    },
    alertId: {
        type: 'id',
        index: true,
    },
    displayName: {
        type: 'string-required',
    },
    contactMethodSet: {
        type: 'string-array-required',
        index: true,
    },
    contactEmails: {
        type: 'string-array-required',
    },
    contactPhoneNumbers: {
        type: 'string-array',
    },
    contactPhoneNumbersWithType: {
        type: 'string-array',
    },
    contactPhoneNumberTypeSet: {
        type: 'string-array-required',
        index: true,
    },
    pagerNumbers: {
        type: 'string-array',
    },
    pagerEmails: {
        type: 'string-array',
    },
    importRow: {
        type: 'number',
        index: true,
    },
    search: { type: 'full-text-search-required', language: 'english' },
});
export const externalRecipientEntityName = 'externalRecipient';
export const externalRecipient = defineEntity({
    version: 5,
    columns: externalRecipientColumns,
    onMigrate: (params) => params.oldData,
    // Frontend calls should always throw error
    // Access to external recipients depends on whether user can access alert, but there is no way to check that from here
    // So external recipients fetching will be done through platform functions
    onAuthGet: () => {
        throw new Error('Unauthorized');
    },
    onAuthList: () => {
        throw new Error('Unauthorized');
    },
    onAuthPost: () => {
        throw new Error('Unauthorized');
    },
    onTransformColumns: (_context, params) => {
        var _a, _b, _c, _d, _e, _f;
        const pagerEmails = [];
        const pagerNumbers = [];
        const contactPhoneNumbersWithType = [];
        const contactPhoneNumberTypeSet = [];
        const contactMethodSet = [];
        if (((_a = params.data.contactPhoneNumbers) !== null && _a !== void 0 ? _a : []).findIndex((c) => c.type.voice) !== -1) {
            contactMethodSet.push('voice');
        }
        if (((_b = params.data.contactPhoneNumbers) !== null && _b !== void 0 ? _b : []).findIndex((c) => c.type.text) !== -1) {
            contactMethodSet.push('text');
        }
        if (((_c = params.data.contactPhoneNumbers) !== null && _c !== void 0 ? _c : []).findIndex((c) => c.type.fax) !== -1) {
            contactMethodSet.push('fax');
        }
        if (params.data.contactEmails.length > 0) {
            contactMethodSet.push('email');
        }
        if (params.data.pagerNumbers && params.data.pagerNumbers.length > 0) {
            contactMethodSet.push('pager');
        }
        (_d = params.data.pagerNumbers) === null || _d === void 0 ? void 0 : _d.map((pager) => {
            pagerNumbers.push(pager.number);
            pagerEmails.push(pager.number + '@' + pager.carrier);
        });
        const applyTypeToPhoneNumber = (type, phoneNumber) => {
            contactPhoneNumbersWithType.push(phoneNumber + '_' + type);
            if (!contactPhoneNumberTypeSet.includes(type)) {
                contactPhoneNumberTypeSet.push(type);
            }
        };
        if (params.data.contactPhoneNumbers) {
            for (const contactPhoneNumber of params.data.contactPhoneNumbers) {
                if (contactPhoneNumber.type.voice) {
                    applyTypeToPhoneNumber('voice', contactPhoneNumber.phoneNumber);
                }
                if (contactPhoneNumber.type.text) {
                    applyTypeToPhoneNumber('text', contactPhoneNumber.phoneNumber);
                }
                if (contactPhoneNumber.type.fax) {
                    applyTypeToPhoneNumber('fax', contactPhoneNumber.phoneNumber);
                }
            }
        }
        const searchTerms = [
            params.data.firstName,
            params.data.lastName,
            ...params.data.contactEmails,
            ...params.data.contactEmails.map((email) => email.replace('@', ' ')),
            ...(params.data.contactPhoneNumbers ? (_e = params.data.contactPhoneNumbers) === null || _e === void 0 ? void 0 : _e.map((contact) => contact.phoneNumber) : []),
            ...contactPhoneNumbersWithType,
            ...pagerNumbers,
        ];
        return {
            importId: params.data.importId,
            alertId: params.data.alertId,
            displayName: getDisplayName(params.data.firstName, params.data.lastName),
            contactMethodSet,
            contactEmails: params.data.contactEmails,
            contactPhoneNumbers: (_f = params.data.contactPhoneNumbers) === null || _f === void 0 ? void 0 : _f.map((contact) => contact.phoneNumber),
            contactPhoneNumbersWithType: contactPhoneNumbersWithType,
            contactPhoneNumberTypeSet: contactPhoneNumberTypeSet,
            pagerNumbers,
            pagerEmails,
            importRow: params.data.importRow,
            search: searchTerms.join(' '),
        };
    },
    onMassUpdateTransform: (context, params) => {
        return {
            alertId: params.data.alertId,
        };
    },
});
export function getExternalRecipientsStorage(context) {
    return context.storage(externalRecipientEntityName);
}
